import React, { useCallback } from 'react';
import {
    dismissHasMinimumRecommendedNewportVersion,
    dismissHasMinimumRequiredNewportVersionDismissed,
} from 'newport/store/newportAppSlice';
import { Icon } from '@instinet-analytics/semantic-ui-components';

import './NewportAppVersionWarning.scss';
import { useDispatch } from 'store/hooks';
import { Tooltip } from '@instinet-analytics/core-lib';
import {
    useSelectNewportVersionDetails,
    useShowRecommendedWarning,
    useShowRequiredWarning,
} from 'newport/store/hooks';

export function NewportAppVersionWarning({
    showRecommended,
}: {
    showRecommended: boolean;
}) {
    const showRequiredWarning = useShowRequiredWarning();
    const showRecommendedWarning = useShowRecommendedWarning();

    if (showRequiredWarning) {
        return <NewportAppRequiredVersionWarning />;
    }

    if (showRecommendedWarning && showRecommended) {
        return <NewportAppRecommendedVersionWarning />;
    }

    return <></>;
}

export function NewportAppRequiredVersionWarning() {
    const versionDetails = useSelectNewportVersionDetails();

    const dispatch = useDispatch();

    const onClickDismiss = useCallback(() => {
        dispatch(dismissHasMinimumRequiredNewportVersionDismissed());
    }, [dispatch]);

    if (!versionDetails) {
        return <></>;
    }

    const sanitizedVersion = sanitizeClientVersion(
        versionDetails.clientNewportVersion
    );

    const text = `Your Newport version ${versionDetails.clientNewportVersion} is below the required version ${versionDetails.minRecommendedNewportVersion}`;
    const sanitisedText = `Your Newport version ${sanitizedVersion} is below the required version ${versionDetails.minRecommendedNewportVersion}`;

    return (
        <div className="NewportAppVersionWarning NewportAppRequiredVersionWarning">
            <div>
                <div className="warning-icon">
                    <Icon name="warning circle" />
                </div>
                <div className="text">
                    <Tooltip text={text}>{sanitisedText}</Tooltip>{' '}
                </div>
                <div className="link" onClick={onClickDismiss}>
                    Dismiss
                </div>
            </div>
        </div>
    );
}

export function NewportAppRecommendedVersionWarning() {
    const versionDetails = useSelectNewportVersionDetails();

    const dispatch = useDispatch();

    const onClickDismiss = useCallback(() => {
        dispatch(dismissHasMinimumRecommendedNewportVersion());
    }, [dispatch]);

    if (!versionDetails) {
        return <></>;
    }

    const sanitizedVersion = sanitizeClientVersion(
        versionDetails.clientNewportVersion
    );
    const text = `Your Newport version ${versionDetails.clientNewportVersion} is below the recommended version ${versionDetails.minRecommendedNewportVersion}`;
    const sanitisedText = `Your Newport version ${sanitizedVersion} is below the recommended version ${versionDetails.minRecommendedNewportVersion}`;

    return (
        <div className="NewportAppVersionWarning NewportAppRecommendedVersionWarning">
            <div>
                <div className="info-icon">
                    <Icon name="info" />
                </div>
                <div className="text">
                    <Tooltip text={text}>{sanitisedText}</Tooltip>{' '}
                </div>
                <div className="link" onClick={onClickDismiss}>
                    Dismiss
                </div>
            </div>
        </div>
    );
}

function sanitizeClientVersion(clientNewportVersion: string) {
    const idx = clientNewportVersion.indexOf('_');
    return idx > -1
        ? `${clientNewportVersion.substring(
              0,
              idx
          )} ${clientNewportVersion.substring(idx)}`
        : clientNewportVersion;
}
